/**
 * DashboardTab: tab "Giám sát" — grid + filter + fullscreen modal.
 */
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Filter, Search } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { employeeService } from '@/services/employee.service'
import { ScreenGrid } from './screen-grid'
import { FullscreenView } from './fullscreen-view'
import { useScreenStoreInit } from '@/hooks/use-screen-store'
import type { Employee } from '@monitor/shared'

export default function DashboardTab() {
  useScreenStoreInit()
  const [search, setSearch] = useState('')
  const [department, setDepartment] = useState<string>('all')
  const [selected, setSelected] = useState<Employee | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['employees', { pageSize: 100 }],
    queryFn: () => employeeService.list({ pageSize: 100, isActive: true }),
    refetchInterval: 60_000,
  })

  const employees = data?.items ?? []
  const departments = useMemo(() => {
    const set = new Set<string>()
    for (const e of employees) if (e.department) set.add(e.department)
    return Array.from(set).sort()
  }, [employees])

  const filtered = useMemo(() => {
    return employees.filter((e) => {
      if (department !== 'all' && e.department !== department) return false
      if (!search) return true
      const q = search.toLowerCase()
      return (
        e.fullName.toLowerCase().includes(q) ||
        e.employeeCode.toLowerCase().includes(q)
      )
    })
  }, [employees, search, department])

  return (
    <div className="container space-y-4 py-4">
      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 py-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Tìm theo tên hoặc mã NV…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <select
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="all">Tất cả phòng ban</option>
              {departments.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
          <div className="text-sm text-muted-foreground">
            Hiển thị {filtered.length}/{employees.length} nhân viên
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">Đang tải…</div>
      ) : (
        <ScreenGrid employees={filtered} onSelect={setSelected} />
      )}

      <FullscreenView employee={selected} open={!!selected} onClose={() => setSelected(null)} />
    </div>
  )
}
